/**
 * Navegación principal vertical.
 *
 * Desktop (>= lg): columna fija, colapsable a solo-iconos. El estado colapsado
 * se persiste en localStorage.
 * Móvil (< lg): drawer off-canvas controlado por AppLayout, con overlay,
 * cierre por Escape y foco devuelto al botón que lo abrió.
 */
import { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { getMiEspacio } from "../api/miEspacio";
import { buildNav } from "./nav-items";

interface Props {
  /** Prefijo de las rutas del policlínico. Ver `useBase()`. */
  base: string;
  /** Drawer móvil abierto. */
  mobileOpen: boolean;
  onMobileClose: () => void;
  /** Colapsado a solo-iconos en desktop. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export function AppSidebar({
  base,
  mobileOpen,
  onMobileClose,
  collapsed,
  onToggleCollapsed,
}: Props) {
  const { me } = useAuth();
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement | null>(null);

  const groups = buildNav(
    base,
    me?.permisos ?? [],
    me?.funcionalidades ?? [],
    me?.is_super_admin ?? false,
  );

  // Contador de pendientes junto a "Mi Espacio". Es lo que hace que alguien se
  // entere de que le llegó trabajo sin tener que ir a mirar.
  const { data: espacio } = useQuery({
    queryKey: ["mi-espacio"],
    queryFn: getMiEspacio,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const pendientes = espacio?.total_pendiente ?? 0;

  /**
   * Cuántos avisos lleva cada entrada del menú.
   *
   * Salen de las mismas bandejas, así que no hay una segunda cuenta que
   * mantener ni otra petición: si la bandeja dice que hay dos pagos por
   * revisar, la bolita de Citas dice dos.
   *
   * Solo en lo que tiene a alguien esperando. Una bolita sobre lo que
   * simplemente existe deja de significar algo en dos días y se aprende a
   * ignorarla — y entonces tampoco se ve la que sí importa.
   */
  function avisosDe(destino: string): number {
    const bandeja = (codigo: string) =>
      espacio?.bandejas.find((b) => b.codigo === codigo)?.total ?? 0;
    if (destino.endsWith("/mi-espacio")) return pendientes;
    // Citas: alguien pidió hora desde su app, o pagó y falta confirmarle.
    if (destino.endsWith("/citas")) return bandeja("solicitudes") + bandeja("validar_pago");
    if (destino.endsWith("/caja")) return bandeja("cobrar") + bandeja("validar_pago");
    if (destino.endsWith("/laboratorio")) return bandeja("lab_procesar");
    if (destino.endsWith("/farmacia/despacho")) return bandeja("dispensar");
    return 0;
  }

  // Al navegar, el drawer móvil se cierra solo.
  useEffect(() => {
    onMobileClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Escape cierra el drawer; al abrirlo mandamos el foco adentro para que
  // teclado y lector de pantalla no queden atrás del overlay.
  useEffect(() => {
    if (!mobileOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onMobileClose();
    }
    document.addEventListener("keydown", onKeyDown);
    panelRef.current?.querySelector<HTMLElement>("a, button")?.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen, onMobileClose]);

  const width = collapsed ? "lg:w-[4.5rem]" : "lg:w-64";

  return (
    <>
      {/* Overlay del drawer móvil */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* En móvil es un panel fijo off-canvas; en lg pasa a sticky dentro del
          flex. inset-y-auto en lg porque top+bottom simultáneos hacen ambiguo
          el anclaje de position:sticky. */}
      <div
        ref={panelRef}
        className={`fixed inset-y-0 left-0 z-40 w-64 shrink-0 border-r border-slate-200 bg-white transition-transform duration-200 lg:sticky lg:inset-y-auto lg:top-0 lg:h-screen lg:translate-x-0 lg:transition-[width] ${width} ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Cabecera del drawer: solo móvil, el logo en desktop vive en el header */}
          <div className="flex h-14 items-center justify-between px-4 lg:hidden">
            <span className="text-sm font-semibold text-slate-900">Menú</span>
            <button
              onClick={onMobileClose}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400"
              aria-label="Cerrar menú"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav
            id="nav-principal"
            aria-label="Navegación principal"
            className="flex-1 overflow-y-auto px-3 py-4"
          >
            {groups.map((group, gi) => (
              <div key={group.id} className={gi > 0 ? "mt-6" : ""}>
                {group.label && (
                  <h2
                    id={`nav-grupo-${group.id}`}
                    className={`px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 ${
                      collapsed ? "lg:sr-only" : ""
                    }`}
                  >
                    {group.label}
                  </h2>
                )}
                <ul
                  className="space-y-0.5"
                  aria-labelledby={group.label ? `nav-grupo-${group.id}` : undefined}
                >
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.end}
                          title={collapsed ? item.label : undefined}
                          className={({ isActive }) =>
                            `group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                              isActive
                                ? "font-medium"
                                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                            } ${collapsed ? "lg:justify-center lg:px-0" : ""}`
                          }
                          style={({ isActive }) =>
                            isActive
                              ? {
                                  // Derivado del color de marca del tenant, así el
                                  // activo siempre combina con el branding.
                                  backgroundColor:
                                    "color-mix(in srgb, var(--color-primary) 12%, white)",
                                  color: "var(--color-primary)",
                                }
                              : undefined
                          }
                        >
                          {({ isActive }) => (
                            <>
                              {isActive && (
                                <span
                                  aria-hidden="true"
                                  className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r"
                                  style={{ backgroundColor: "var(--color-primary)" }}
                                />
                              )}
                              <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
                              <span className={collapsed ? "lg:sr-only" : "truncate"}>
                                {item.label}
                              </span>
                              {avisosDe(item.to) > 0 && (
                                <span
                                  className={`ml-auto rounded-full bg-red-600 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white ${
                                    collapsed
                                      ? "lg:absolute lg:right-2 lg:top-1 lg:ml-0"
                                      : ""
                                  }`}
                                  aria-label={`${avisosDe(item.to)} pendientes`}
                                >
                                  {avisosDe(item.to)}
                                </span>
                              )}
                            </>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>

          {/* Colapsar: solo desktop, no tiene sentido en el drawer */}
          <div className="hidden border-t border-slate-200 p-2 lg:block">
            <button
              onClick={onToggleCollapsed}
              aria-expanded={!collapsed}
              aria-controls="nav-principal"
              aria-label={collapsed ? "Expandir navegación" : "Colapsar navegación"}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 ${
                collapsed ? "justify-center px-0" : ""
              }`}
            >
              {collapsed ? (
                <PanelLeftOpen className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
              ) : (
                <PanelLeftClose className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
              )}
              <span className={collapsed ? "sr-only" : ""}>Colapsar</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
